export enum FieldMarkerType {
  FLAG = 'flag',
  OBSTACLE = 'obstacle',
  CUSTOM = 'custom',
}

export enum FieldMarkerIcon {
  FLAG = 'flag',
  OBSTACLE = 'obstacle',
  HOLE = 'hole',
  ANTHILL = 'anthill',
  ROCK = 'rock',
  TREE = 'tree',
  WATER = 'water',
}

// Normative fallback (AGP v1.3 spec): an icon outside the enum above is
// displayed as the generic flag glyph, never as a broken image.
export const DEFAULT_FIELD_MARKER_ICON = FieldMarkerIcon.FLAG;

export interface FieldMarkerProperties {
  id: number;
  type: FieldMarkerType | string;
  name: string;
  icon: FieldMarkerIcon | string;
  color: string;
  offsetM: number;
  origin: number;
  fixQuality: number;
  timestampMs: number;
}

export interface FieldMarkerFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
  properties: FieldMarkerProperties;
  show?: boolean;
}

const ICON_VIEWBOX_SIZE = 80;
const ICON_DISC_CENTER = 40;
const ICON_DISC_RADIUS = 40;
const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/;
// Fallback disc color when `properties.color` is missing or not a valid hex
// color — matches the neutral icon tone already used elsewhere on this screen.
const DEFAULT_FIELD_MARKER_COLOR = '#4f4350';

/**
 * AGP v1.3 field marker icon glyphs (`markers/.../field_marks.geojsonl`).
 * Each glyph is authored white-on-transparent in an 80x80 viewBox, meant to
 * be composited over a disc colored with the marker's own `properties.color`
 * — see `buildFieldMarkerIconUrl`.
 *
 * Source: original artwork by Agres, except FieldMarkerIcon.TREE, derived
 * from Material Symbols "park" (Google), Apache License 2.0
 * (http://www.apache.org/licenses/LICENSE-2.0).
 */
const FIELD_MARKER_ICON_GLYPHS: Record<FieldMarkerIcon, string> = {
  // Scaled/recentered (see transform on each <g>) so the glyph stays fully
  // inside the disc drawn by `buildFieldMarkerIconUrl` instead of spilling
  // past its circular edge or looking sparse relative to it.
  [FieldMarkerIcon.FLAG]:
    '<g transform="translate(40,40) scale(0.75) translate(-40,-40)">' +
    '<path d="M15 0V80" stroke="#FFFFFF" stroke-width="7"/>' +
    '<path d="M15 8C33 0 48 16 65 8V46C48 54 33 38 15 46" stroke="#FFFFFF" stroke-width="7"/>' +
    '</g>',
  [FieldMarkerIcon.OBSTACLE]:
    '<g transform="translate(40,40) scale(0.7) translate(-40,-40)">' +
    '<path d="M40 56.8519C39.1524 56.8519 38.3238 57.08 37.619 57.5073C36.9142 57.9346 36.3649 58.542 36.0406 59.2526C35.7162 59.9632 35.6313 60.7451 35.7967 61.4995C35.962 62.2539 36.3702 62.9468 36.9696 63.4907C37.569 64.0345 38.3326 64.4049 39.1639 64.555C39.9953 64.705 40.857 64.628 41.6401 64.3337C42.4232 64.0393 43.0926 63.5409 43.5635 62.9014C44.0344 62.2618 44.2858 61.51 44.2858 60.7408C44.2858 59.7094 43.8342 58.7203 43.0305 57.9909C42.2268 57.2616 41.1367 56.8519 40 56.8519Z" fill="white"/>' +
    '<path d="M42.8572 28.3334H37.1429V51.6667H42.8572V28.3334Z" fill="white"/>' +
    '<path d="M77.1428 75H2.85716C2.36629 75 1.88369 74.8852 1.45591 74.6668C1.02812 74.4483 0.669571 74.1336 0.414834 73.7528C0.160098 73.3721 0.0177644 72.9382 0.00155789 72.493C-0.0146486 72.0478 0.0958181 71.6064 0.322308 71.2112L37.4651 6.39641C37.7064 5.97546 38.0703 5.62266 38.5173 5.37643C38.9643 5.1302 39.4771 5 40 5C40.5229 5 41.0357 5.1302 41.4827 5.37643C41.9297 5.62266 42.2936 5.97546 42.5349 6.39641L79.6777 71.2112C79.9042 71.6064 80.0146 72.0478 79.9984 72.493C79.9822 72.9382 79.8399 73.3721 79.5852 73.7528C79.3304 74.1336 78.9719 74.4483 78.5441 74.6668C78.1163 74.8852 77.6337 75 77.1428 75ZM7.57345 69.8148H72.4266L72.4323 69.8063L40.0057 13.2222H39.9943L7.56773 69.8063L7.57345 69.8148Z" fill="white"/>' +
    '</g>',
  [FieldMarkerIcon.HOLE]:
    '<ellipse cx="40" cy="42" rx="32" ry="16" fill="none" stroke="white" stroke-width="7"/>' +
    '<path d="M60.12 42 C59.44 41.48 58.42 40.84 57.1 40.23 C55.79 39.63 54.18 39.04 52.38 38.55 C50.58 38.07 48.59 37.67 46.49 37.4 C44.39 37.14 42.2 37 40 37 C37.8 37 35.61 37.14 33.51 37.4 C31.41 37.67 29.42 38.07 27.62 38.55 C25.82 39.04 24.21 39.63 22.9 40.23 C21.58 40.84 20.56 41.48 19.88 42" fill="none" stroke="white" stroke-width="7" stroke-linejoin="miter"/>',
  [FieldMarkerIcon.ANTHILL]:
    '<g transform="translate(4,6.7) scale(0.9)">' +
    '<path d="M36 12 L28 4" fill="none" stroke="white" stroke-width="7"/>' +
    '<path d="M44 12 L52 4" fill="none" stroke="white" stroke-width="7"/>' +
    '<path d="M34 27 L18 17" fill="none" stroke="white" stroke-width="7"/>' +
    '<path d="M46 27 L62 17" fill="none" stroke="white" stroke-width="7"/>' +
    '<path d="M34 36 L18 46" fill="none" stroke="white" stroke-width="7"/>' +
    '<path d="M46 36 L62 46" fill="none" stroke="white" stroke-width="7"/>' +
    '<circle cx="40" cy="18" r="6.5" fill="white"/>' +
    '<circle cx="40" cy="31" r="6" fill="white"/>' +
    '<ellipse cx="40" cy="55" rx="12" ry="15" fill="none" stroke="white" stroke-width="7"/>' +
    '</g>',
  [FieldMarkerIcon.ROCK]:
    '<g transform="translate(-9.2,-15.2) scale(1.2)">' +
    '<path d="M17 66 L15 50 L24 38 L33 41 L40 26 L54 27 L66 42 L67 66 Z" fill="none" stroke="white" stroke-width="7" stroke-linejoin="miter"/>' +
    '</g>',
  [FieldMarkerIcon.TREE]:
    '<g transform="translate(0 80) scale(0.0833333)">' +
    '<path d="M423-229H177q-18 0-27-16t2-31l157-227h-37q-18.38 0-27.19-16-8.81-16 2.19-31l208-295q4.57-6.12 11.43-9.56 6.86-3.44 13.71-3.44 6.86 0 13.36 3.5Q500-851 505-845l208 295q11 15 2.19 31T688-503h-36l156 227q11 15 2 31t-27 16H538v119q0 12.75-8.5 21.37Q521-80 508.48-80h-55.96Q440-80 431.5-88.63 423-97.25 423-110v-119Z" fill="white"/>' +
    '</g>',
  [FieldMarkerIcon.WATER]:
    '<path d="M40 6.7C40 6.7 16.7 35.4 16.7 51.7C16.7 63.6 27.1 73.3 40 73.3C52.9 73.3 63.3 63.6 63.3 51.7C63.3 35.4 40 6.7 40 6.7Z" fill="white"/>',
};

export function isKnownFieldMarkerIcon(icon: string): icon is FieldMarkerIcon {
  return Object.values(FieldMarkerIcon).includes(icon as FieldMarkerIcon);
}

/**
 * Builds a Google Maps marker icon URL for an AGP v1.3 field marker: a disc
 * filled with the marker's own `color`, with its white icon glyph on top.
 *
 * @remarks
 * The backend parser already normalizes `properties.icon` to a known value
 * (see `agp.js#transformMarkers`), but the fallback to the flag glyph is
 * repeated here defensively in case a marker was loaded from stale cached
 * data — the AGP v1.3 spec requires this glyph never renders as broken.
 */
export function buildFieldMarkerIconUrl(color: string, icon: string): string {
  let resolvedIcon: FieldMarkerIcon = DEFAULT_FIELD_MARKER_ICON;
  if (isKnownFieldMarkerIcon(icon)) {
    resolvedIcon = icon;
  }
  let resolvedColor = DEFAULT_FIELD_MARKER_COLOR;
  if (HEX_COLOR_PATTERN.test(color)) {
    resolvedColor = color;
  }
  const glyph = FIELD_MARKER_ICON_GLYPHS[resolvedIcon];
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${ICON_VIEWBOX_SIZE}" height="${ICON_VIEWBOX_SIZE}" viewBox="0 0 ${ICON_VIEWBOX_SIZE} ${ICON_VIEWBOX_SIZE}" fill="none">` +
    `<circle cx="${ICON_DISC_CENTER}" cy="${ICON_DISC_CENTER}" r="${ICON_DISC_RADIUS}" fill="${resolvedColor}"/>` +
    glyph +
    '</svg>';
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}
